import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  input,
  inject,
  type Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { PushPipe } from '@ngrx/component';
import type { Observable } from 'rxjs';
import { map, startWith, switchMap, combineLatest, of, merge } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  IconComponent,
  OverlayOriginDirective,
  OverlayPosition,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { ViewFilterDef, ViewFilterInputDef, FilterControl } from '@core/views-types';

import { QuickFilterComponent } from './quick-filter/quick-filter.component';
import { FiltersContainerComponent } from '../../advanced-filters/filters-container.component';
import { FiltersFormDataService } from '../../services/filters-form-data.service';
import { FiltersFormStateService } from '../../services/filters-form-state.service';
import { FiltersFormService } from '../../services/filters-form.service';

interface QuickFilterData {
  filterDef: ViewFilterDef;
  filterControl: FilterControl;
  filterInputDefs: ViewFilterInputDef[];
  filterState: unknown[];
  filterUuid: string;
  dataSourceId: string;
  valueText$: Observable<string>;
}

@Component({
  selector: 'rt-views-quick-filters-container',
  imports: [
    ConnectedOverlayDirective,
    ButtonComponent,
    BadgeComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    IconComponent,
    FiltersContainerComponent,
    QuickFilterComponent,
    PushPipe,
    TranslatePipe,
  ],
  templateUrl: './quick-filters-container.component.html',
  styleUrls: ['./quick-filters-container.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickFiltersContainerComponent {
  private readonly filtersFormService = inject(FiltersFormService);
  private readonly filtersFormDataService = inject(FiltersFormDataService);
  private readonly filtersFormStateService = inject(FiltersFormStateService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly OverlayPosition = OverlayPosition;

  public viewsRecordsTotalCount = input<number | null>(null);
  public hasAdvancedFilters = input(true);
  public hasClearFilters = input(false);

  public allFiltersCount = 0;
  public filterData: Signal<QuickFilterData[]> = toSignal(
    this.filtersFormDataService.quickFiltersData$,
    { initialValue: [] },
  );
  public anyQuickFilterActive = computed(() =>
    this.filterData().some(filter => filter.filterControl.valid),
  );

  private untilDestroyed = injectUntilDestroyed();

  public ngOnInit() {
    this.watchPinnedFiltersStatus();
    this.watchFiltersCount();
  }

  public onQuickFilterClear(filterControl: FilterControl) {
    this.filtersFormService.removeFilterById(filterControl.value.id!);
    this.filtersFormStateService.filtersForm.markAsDirty();
    this.cdRef.detectChanges();
  }

  public onFilterStateChanges(uuid: string, state: unknown[]) {
    this.filtersFormStateService.updateFilterState(uuid, state);
  }

  protected onClearAllFilters() {
    this.filterData().forEach(filter => {
      this.filtersFormService.removeFilterById(filter.filterControl.value.id!);
    });
    this.filtersFormStateService.filtersForm.markAsDirty();
    this.cdRef.detectChanges();
  }

  private watchFiltersCount() {
    const { filtersForm } = this.filtersFormStateService;
    merge(filtersForm.controls.filters.valueChanges, this.filtersFormDataService.quickFiltersData$)
      .pipe(
        map(() => filtersForm.value.filters!),
        map(filters => filters.length),
        startWith(filtersForm.value.filters?.length ?? 0),
        this.untilDestroyed,
      )
      .subscribe(allFiltersCount => {
        this.allFiltersCount = allFiltersCount;
        this.cdRef.detectChanges();
      });
  }

  private watchPinnedFiltersStatus() {
    const { filters } = this.filtersFormStateService.filtersForm.controls;
    this.filtersFormStateService.quickFilterControls$
      .pipe(
        switchMap(quickFilterControls =>
          merge(
            ...quickFilterControls.map(quickFilterControl =>
              combineLatest({
                status: quickFilterControl.statusChanges.pipe(startWith(quickFilterControl.status)),
                filterControl: of(quickFilterControl),
              }),
            ),
          ),
        ),
        this.untilDestroyed,
      )
      .subscribe(({ filterControl, status }) => {
        const index = filters.controls.findIndex(filter => filter === filterControl);
        if (index === -1 && status === 'VALID') {
          filters.push(filterControl);
          filters.markAsDirty();
        } else if (index !== -1 && status !== 'VALID') {
          filters.removeAt(index);
          filters.markAsDirty();
        }
      });
  }
}
